import React from 'react';
import * as Inovx from '@shared';
import { ColorPickerComponent } from '@syncfusion/ej2-react-inputs';

import SettingActions from './../actions/SettingActions';
import PanoViewerConstants from './../constants/PanoViewerConstants';
import ViewerStore from '../stores/ViewerStore';
import { QualityConfig } from '../viewers/Data/WebData';

var SystemActions = Inovx.Shared.System.Actions;

interface FacilityPreferencesProps {
    onClose: () => void;
}

interface IColorPickerValue {
    colorValue: string;
    opacityValue: number;
}
function ToFacilitySetting(preferences: { [id: string]: string }): IFacilitySettings {
    var viewerSetting: ISetting[] = [];
    viewerSetting.push({
        Name: PanoViewerConstants.PanoCompass,
        Value: preferences[PanoViewerConstants.PanoCompass]
    });
    viewerSetting.push({
        Name: PanoViewerConstants.PanoScanSphereColor,
        Value: preferences[PanoViewerConstants.PanoScanSphereColor]
    });
    viewerSetting.push({
        Name: PanoViewerConstants.PanoImageSphereColor,
        Value: preferences[PanoViewerConstants.PanoImageSphereColor]
    });
    viewerSetting.push({
        Name: PanoViewerConstants.TagVolumeBoxColor,
        Value: preferences[PanoViewerConstants.TagVolumeBoxColor]
    });
    viewerSetting.push({
        Name: PanoViewerConstants.UnitMeasurement,
        Value: preferences[PanoViewerConstants.UnitMeasurement]
    });
    viewerSetting.push({
        Name: PanoViewerConstants.MeasurementColor,
        Value: preferences[PanoViewerConstants.MeasurementColor]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.BackgroundColor,
        Value: preferences[PanoViewerConstants.BackgroundColor]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.ZoomSlider,
        Value: preferences[PanoViewerConstants.ZoomSlider]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.EdgeShader,
        Value: preferences[PanoViewerConstants.EdgeShader]
    });

    // 3D Memory profile
    viewerSetting.push({
        Name: PanoViewerConstants.MemoryProfile,
        Value: preferences[PanoViewerConstants.MemoryProfile]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.CameraType,
        Value: preferences[PanoViewerConstants.CameraType]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.ShowCrosshair,
        Value: preferences[PanoViewerConstants.ShowCrosshair]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.RotationSensitivity,
        Value: preferences[PanoViewerConstants.RotationSensitivity]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.VelocitySensitivity,
        Value: preferences[PanoViewerConstants.VelocitySensitivity]
    });

    viewerSetting.push({
        Name: PanoViewerConstants.InvertY,
        Value: preferences[PanoViewerConstants.InvertY]
    });

    var facilitySetting: IFacilitySettings = {
        ViewerSettings: viewerSetting,
    };

    return facilitySetting;
}

function convertSettings(settings: IFacilityConfig) {
    var preferences: { [id: string]: string } = {};
    preferences[PanoViewerConstants.PanoCompass] = settings.PanoCompassVisible ? PanoViewerConstants.Show : PanoViewerConstants.Hide;
    preferences[PanoViewerConstants.PanoScanSphereColor] = settings.PanoScanSphereColor;
    preferences[PanoViewerConstants.PanoImageSphereColor] = settings.PanoImageSphereColor;
    preferences[PanoViewerConstants.TagVolumeBoxColor] = settings.TagVolumeBoxColor;
    preferences[PanoViewerConstants.UnitMeasurement] = settings.UnitMeasurement;
    preferences[PanoViewerConstants.MeasurementColor] = settings.MeasurementColor;
    preferences[PanoViewerConstants.BackgroundColor] = settings.BackgroundColor;
    preferences[PanoViewerConstants.ZoomSlider] = settings.ZoomSliderVisible ? PanoViewerConstants.On : PanoViewerConstants.Off;
    preferences[PanoViewerConstants.EdgeShader] = settings.EdgeShader ? PanoViewerConstants.On : PanoViewerConstants.Off;

    const cameraFromConfig = (settings as any).CameraType as string | undefined;
    preferences[PanoViewerConstants.CameraType] = cameraFromConfig ?? PanoViewerConstants.CameraTypeExamine;

    const crossHairFromConfig = (settings as any).ShowCrosshair as string | undefined;
    preferences[PanoViewerConstants.ShowCrosshair] = crossHairFromConfig ? PanoViewerConstants.On : PanoViewerConstants.Off;

    const rotFromConfig = (settings as any).RotationSensitivity as string | number | undefined;
    preferences[PanoViewerConstants.RotationSensitivity] = (rotFromConfig ?? 0.0).toString();

    const velFromConfig = (settings as any).VelocitySensitivity as string | number | undefined;
    preferences[PanoViewerConstants.VelocitySensitivity] = (velFromConfig ?? 0.0).toString();

    const invertYFromConfig = (settings as any).InvertY as string | undefined;
    preferences[PanoViewerConstants.InvertY] = invertYFromConfig ? PanoViewerConstants.On : PanoViewerConstants.Off;

    const memoryFromConfig = (settings as any).MemoryProfile as string | undefined;
    preferences[PanoViewerConstants.MemoryProfile] = memoryFromConfig ?? 'Medium';

    return preferences;
}

const FacilityPreferences = (props: FacilityPreferencesProps) => {
    const [preferences, setPreferences] = React.useState<{ [id: string]: string }>(convertSettings(ViewerStore.getSettings()));
    const [dirty, setDirty] = React.useState<boolean>(false);
    const [viewerOpen, setViewerOpen] = React.useState(false);
    const [cameraOpen, setCameraOpen] = React.useState(false);

    const [initialMemoryProfile, setInitialMemoryProfile] = React.useState<string | null>(null);

    // Loaded from /lod/pointcloud-quality.json
    const [qualityConfig, setQualityConfig] = React.useState<QualityConfig | null>(null);
    const [qualityProfiles, setQualityProfiles] = React.useState<string[] | null>(null);
    const [qualityLoadError, setQualityLoadError] = React.useState<string | null>(null);

    React.useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const url = Inovx.Shared.Common.UrlUtilities.getBaseApplicationUrl() + "/lod/pointcloud-quality.json";
                const r = await fetch(url, { cache: 'no-cache' });
                if (!r.ok) throw new Error("Failed to load pointcloud-quality.json: " + r.status);
                const cfg = (await r.json()) as QualityConfig;
                if (!isMounted) return;
                setQualityConfig(cfg);
                const keys = Object.keys(cfg.profiles || {});
                setQualityProfiles(keys);
                // If no value yet, seed from cfg.settings.Quality or first key
                setPreferences(prev => {
                    if (prev[PanoViewerConstants.MemoryProfile]) return prev;
                    const seed = cfg.settings?.Quality ?? keys[0] ?? 'Medium';
                    return { ...prev, [PanoViewerConstants.MemoryProfile]: seed };
                });
            } catch (e: any) {
                if (!isMounted) return;
                setQualityLoadError(e?.message || String(e));
                // Ensure we still have some value for the selector so save works.
                setPreferences(prev => ({
                    ...prev,
                    [PanoViewerConstants.MemoryProfile]: prev[PanoViewerConstants.MemoryProfile] || 'Medium'
                }));
            }
        })();
        return () => { isMounted = false; };
    }, []);

    React.useEffect(() => {
        const current = preferences[PanoViewerConstants.MemoryProfile];
        if (initialMemoryProfile === null && current) {
            setInitialMemoryProfile(current);
        }
    }, [preferences, initialMemoryProfile]);

    const onCommpassChanged = (key: string) => {
        setPreferences(prev => {
            const nextVal = prev[key] === PanoViewerConstants.Show ? PanoViewerConstants.Hide : PanoViewerConstants.Show;
            setDirty(true);
            return { ...prev, [key]: nextVal };
        });
    };

    const onCrosshairChanged = (key: string) => {
        setPreferences(prev => {
            const nextVal = prev[key] === PanoViewerConstants.Show ? PanoViewerConstants.Show : PanoViewerConstants.Hide;
            setDirty(true);
            return { ...prev, [key]: nextVal };
        });
    };

    const on3DCheckboxChanged = (key: string) => {
        setPreferences(prev => {
            const nextVal = prev[key] === PanoViewerConstants.On ? PanoViewerConstants.Off : PanoViewerConstants.On;
            setDirty(true);
            return { ...prev, [key]: nextVal };
        });
    };

    const onRotationSensitivityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateProperty(PanoViewerConstants.RotationSensitivity, e.target.value);
    };

    const onVelocitySensitivityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateProperty(PanoViewerConstants.VelocitySensitivity, e.target.value);
    };

    const updateProperty = (prop: string, value: any) => {
        setPreferences(prev => ({ ...prev, [prop]: value as any }));
        setDirty(true);
    };

    const onUnitChange = (e: any): void => {
        updateProperty(PanoViewerConstants.UnitMeasurement, e.target.value);
    };

    const onColorChanged = (key: string, color: string): void => {
        updateProperty(key, color);
    };

    const onMemoryChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        updateProperty(PanoViewerConstants.MemoryProfile, e.target.value);
    };

    const onCameraTypeChange = (e: any): void => {
        updateProperty(PanoViewerConstants.CameraType, e.target.value);
    };

    const save = () => {
        var settings = ToFacilitySetting(preferences);
        SettingActions.setSettings(settings)
            .then(() => {
                props.onClose();
            });
    };

    const onSave = (e: React.MouseEvent) => {
        if (e && (e as any).stopPropagation)
            e.stopPropagation();
        save();
    };

    const onCancel = (e: any) => {
        if (dirty) {
            SystemActions.displaySaveChangesDialog({
                message: InovxLoader.t("common:msgBox.saveChanges"),
                confirmCallback: () => {
                    save();
                },
                cancelCallback: () => {
                    props.onClose();
                }
            });
        }
        else {
            props.onClose();
        }
    };

    const onReset = (e: any) => {
        const defaultPreferences = convertSettings(ViewerStore.getDefaultSettings());
        // If we have a loaded config, prefer its default Quality key
        const seeded = (cfg: QualityConfig | null) => cfg?.settings?.Quality || defaultPreferences[PanoViewerConstants.MemoryProfile] || 'Medium';
        defaultPreferences[PanoViewerConstants.MemoryProfile] = seeded(qualityConfig);
        setPreferences(defaultPreferences);
        setDirty(true);
    };

    const _toHex = (e: string) => {
        var arr = e.split(',');
        var retValue = '#';
        for (var i in arr) {
            retValue += ("0" + (parseInt(arr[i]).toString(16))).slice(-2);
        }
        return retValue;
    };

    const _getColorPickerValue = (rgbColor: string): IColorPickerValue => {
        var colorValue = rgbColor;
        var opacityValue = 1;
        if (colorValue.indexOf('rgb(') > -1) {
            colorValue = _toHex(colorValue.replace('rgb(', '').replace(')', ''));
        } else if (colorValue.indexOf('rgba') > -1) {
            var start = colorValue.lastIndexOf(',');
            opacityValue = parseFloat(colorValue.slice(start + 1, colorValue.length - 1));
            colorValue = _toHex(colorValue.substring(0, start).replace('rgba(', ''));
        } else if (colorValue === 'transparent') {
            opacityValue = 0;
            colorValue = '#000000';
        }

        return {
            colorValue: colorValue,
            opacityValue: opacityValue
        };
    };

    const units = [
        { "Name": InovxLoader.t('facility:preferences.unitMillimeter'), "Category": InovxLoader.t('facility:preferences.metric'), "Id": "mm" },
        { "Name": InovxLoader.t('facility:preferences.unitCentimeter'), "Category": InovxLoader.t('facility:preferences.metric'), "Id": "cm" },
        { "Name": InovxLoader.t('facility:preferences.unitMeter'), "Category": InovxLoader.t('facility:preferences.metric'), "Id": "m" },
        { "Name": InovxLoader.t('facility:preferences.unitKilometer'), "Category": InovxLoader.t('facility:preferences.metric'), "Id": "km" },
        { "Name": InovxLoader.t('facility:preferences.unitInch'), "Category": InovxLoader.t('facility:preferences.imperial'), "Id": "in" },
        { "Name": InovxLoader.t('facility:preferences.unitFoot'), "Category": InovxLoader.t('facility:preferences.imperial'), "Id": "ft" },
        { "Name": InovxLoader.t('facility:preferences.unitYard'), "Category": InovxLoader.t('facility:preferences.imperial'), "Id": "yd" },
        { "Name": InovxLoader.t('facility:preferences.unitMile'), "Category": InovxLoader.t('facility:preferences.imperial'), "Id": "mi" },
    ];
    const groupedUnits = units.reduce((groups: any, unit) => {
        const category = unit.Category;
        if (!groups[category]) {
            groups[category] = [];
        }
        groups[category].push(unit);
        return groups;
    }, {} as any);

    const unitGroupFields = { groupBy: 'Category', text: 'Name', value: 'Id' };

    const getQualityProfileLabel = (profile: string) => {
        switch ((profile || '').toLowerCase()) {
            case 'ultra': return InovxLoader.t('facility:preferences.qualityUltra');
            case 'high': return InovxLoader.t('facility:preferences.qualityHigh');
            case 'medium': return InovxLoader.t('facility:preferences.qualityMedium');
            case 'low': return InovxLoader.t('facility:preferences.qualityLow');
            default: return profile;
        }
    };

    return (
        <div className="preferencers-popup no-print">
            <div className="panel panel-primary preferences-app-content">
                <div className="panel-heading">
                    {InovxLoader.t('facility:preferences.title')}
                    <a href="#" onClick={onCancel}>
                        <span className="pull-right glyphicon glyphicon glyphicon-remove"></span>
                    </a>
                </div>

                <div className="panel-body">
                    <div className="panel-group mb-0">

                        {/* =========================
                        Viewer Preferences
                    ========================= */}
                        <div className="panel panel-default">
                            <div className="panel-heading">
                                <h4 className="panel-title">
                                    <a
                                        className={`panel-toggle${viewerOpen ? '' : ' collapsed'}`}
                                        onClick={() => setViewerOpen(o => !o)}
                                    >
                                        {InovxLoader.t("facility:preferences.viewPanel")}
                                    </a>
                                </h4>
                            </div>

                            <div className={`panel-collapse collapse preferences-box${viewerOpen ? ' in' : ''}`}>
                                <div className="panel-body">
                                    <form>
                                        <div className="row override">
                                            <div className="checkbox d-flex w-100" id="compassChk">
                                                <label className="col-sm-9 pull-left p-0 d-flex a-ic">
                                                    {InovxLoader.t("facility:preferences.compassOff")}
                                                </label>
                                                <div className="col-sm-3 d-flex j-ce a-ic">
                                                    <input
                                                        type="checkbox"
                                                        checked={preferences[PanoViewerConstants.PanoCompass] === PanoViewerConstants.Show ? false : true}
                                                        onChange={() => onCommpassChanged(PanoViewerConstants.PanoCompass)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <label className="col-sm-4 pull-left d-flex a-ic">
                                                {InovxLoader.t("facility:preferences.unit")}
                                            </label>
                                            <div className="col-sm-8">
                                                <div className="pull-right">
                                                    <select
                                                        className="unit-selector"
                                                        id="unitSelect"
                                                        onChange={onUnitChange}
                                                        value={preferences[PanoViewerConstants.UnitMeasurement]}
                                                    >
                                                        {Object.keys(groupedUnits).map(category => (
                                                            <optgroup key={category} label={category}>
                                                                {groupedUnits[category].map((unit: any) => (
                                                                    <option key={unit.Id} value={unit.Id}>
                                                                        {unit.Name}
                                                                    </option>
                                                                ))}
                                                            </optgroup>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <label className="col-sm-9 pull-left d-flex a-ic">
                                                {InovxLoader.t("facility:preferences.scanSpheresColor")}
                                            </label>
                                            <div className="col-sm-3">
                                                <div className="pull-right">
                                                    <ColorPickerComponent
                                                        id="color-picker"
                                                        value={preferences[PanoViewerConstants.PanoScanSphereColor]}
                                                        enableOpacity={false}
                                                        change={(e: any) => onColorChanged(PanoViewerConstants.PanoScanSphereColor, e.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <label className="col-sm-9 pull-left d-flex a-ic">
                                                {InovxLoader.t("facility:preferences.imageSpheresColor")}
                                            </label>
                                            <div className="col-sm-3">
                                                <div className="pull-right">
                                                    <ColorPickerComponent
                                                        id="color-picker"
                                                        value={preferences[PanoViewerConstants.PanoImageSphereColor]}
                                                        enableOpacity={false}
                                                        change={(e: any) => onColorChanged(PanoViewerConstants.PanoImageSphereColor, e.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <label className="col-sm-9 pull-left d-flex a-ic">
                                                {InovxLoader.t("facility:preferences.tagVolumeBoxColor")}
                                            </label>
                                            <div className="col-sm-3">
                                                <div className="pull-right">
                                                    <ColorPickerComponent
                                                        id="color-picker"
                                                        value={preferences[PanoViewerConstants.TagVolumeBoxColor]}
                                                        enableOpacity={true}
                                                        change={(e: any) => onColorChanged(PanoViewerConstants.TagVolumeBoxColor, e.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row">
                                            <label className="col-sm-9 pull-left d-flex a-ic">
                                                {InovxLoader.t("facility:preferences.measurementsColor")}
                                            </label>
                                            <div className="col-sm-3">
                                                <div className="pull-right">
                                                    <ColorPickerComponent
                                                        id="color-picker"
                                                        value={preferences[PanoViewerConstants.MeasurementColor]}
                                                        enableOpacity={false}
                                                        change={(e: any) => onColorChanged(PanoViewerConstants.MeasurementColor, e.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row override">
                                            <div className="checkbox d-flex w-100" id="3DEdgeShader">
                                                <label className="col-sm-9 pull-left p-0 d-flex a-ic">
                                                    {InovxLoader.t("facility:preferences.3DEdgeShader")}
                                                </label>
                                                <div className="col-sm-3 d-flex j-ce a-ic">
                                                    <input
                                                        type="checkbox"
                                                        checked={preferences[PanoViewerConstants.EdgeShader] === PanoViewerConstants.On ? true : false}
                                                        onChange={() => on3DCheckboxChanged(PanoViewerConstants.EdgeShader)}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row" id="3DMemory">
                                            <label className="col-sm-9 pull-left d-flex a-ic">
                                                {InovxLoader.t("facility:preferences.3DMemory")}
                                            </label>
                                            <div className="col-sm-3">
                                                <div className="pull-right">
                                                    <select
                                                        className="memory-selector"
                                                        id="memorySelect"
                                                        value={preferences[PanoViewerConstants.MemoryProfile] || ''}
                                                        onChange={onMemoryChange}
                                                        disabled={!qualityProfiles || !!qualityLoadError}
                                                    >
                                                        {qualityProfiles ? (
                                                            qualityProfiles.map(k => (
                                                                <option key={k} value={k}>{getQualityProfileLabel(k)}</option>
                                                            ))
                                                        ) : (
                                                            <option value="">
                                                                {qualityLoadError ? InovxLoader.t('facility:preferences.profilesUnavailable') : InovxLoader.t('facility:preferences.loadingProfiles')}
                                                            </option>
                                                        )}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        {initialMemoryProfile !== null &&
                                            (preferences[PanoViewerConstants.MemoryProfile] || '') !== initialMemoryProfile && (
                                                <div className="row">
                                                    <div className="col-sm-12">
                                                        <p className="help-block" style={{ marginTop: 8, marginBottom: 0, color: '#a94442' }}>
                                                            {InovxLoader.t('facility:preferences.pointResolutionRefreshInfo')}
                                                        </p>
                                                    </div>
                                                </div>
                                            )}

                                        {/* 3D Options */}
                                        {<>
                                            <div className="row">
                                                <label className="col-sm-9 pull-left d-flex a-ic">
                                                    {InovxLoader.t("facility:preferences.3DBackgroundColor")}
                                                </label>
                                                <div className="col-sm-3">
                                                    <div className="pull-right">
                                                        <ColorPickerComponent
                                                            id="color-picker"
                                                            value={preferences[PanoViewerConstants.BackgroundColor]}
                                                            enableOpacity={false}
                                                            change={(e: any) => onColorChanged(PanoViewerConstants.BackgroundColor, e.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row override">
                                                <div className="checkbox d-flex w-100" id="3DZoomSlider">
                                                    <label className="col-sm-9 pull-left p-0 d-flex a-ic">
                                                        {InovxLoader.t("facility:preferences.3DZoomSlider")}
                                                    </label>
                                                    <div className="col-sm-3 d-flex j-ce a-ic">
                                                        <input
                                                            type="checkbox"
                                                            checked={preferences[PanoViewerConstants.ZoomSlider] === PanoViewerConstants.On ? true : false}
                                                            onChange={() => on3DCheckboxChanged(PanoViewerConstants.ZoomSlider)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </>}
                                    </form>
                                </div>
                            </div>
                        </div>

                        {/* =========================
                        Camera Preferences
                    ========================= */}
                        <div className="panel panel-default">
                            <div className="panel-heading">
                                <h4 className="panel-title">
                                    <a
                                        className={`panel-toggle${cameraOpen ? '' : ' collapsed'}`}
                                        onClick={() => setCameraOpen(o => !o)}
                                    >
                                        {InovxLoader.t("facility:preferences.cameraPanel")}
                                    </a>
                                </h4>
                            </div>

                            <div className={`panel-collapse collapse preferences-box${cameraOpen ? ' in' : ''}`}>
                                <div className="panel-body">
                                    <form>
                                        {<>

                                            {/* Crosshair */}
                                            <div className="row override">
                                                <div className="checkbox d-flex w-100" id="crosshairChk">
                                                    <label className="col-sm-9 pull-left p-0 d-flex a-ic">
                                                        {InovxLoader.t("facility:preferences.showCrosshair")}
                                                    </label>
                                                    <div className="col-sm-3 d-flex j-ce a-ic">
                                                        <input
                                                            type="checkbox"
                                                            checked={preferences[PanoViewerConstants.ShowCrosshair] === PanoViewerConstants.On}
                                                            onChange={() => on3DCheckboxChanged(PanoViewerConstants.ShowCrosshair)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Invert Y */}
                                            <div className="row override">
                                                <div className="checkbox d-flex w-100" id="invertYChk">
                                                    <label className="col-sm-9 pull-left p-0 d-flex a-ic">
                                                        {InovxLoader.t("facility:preferences.invertY")}
                                                    </label>
                                                    <div className="col-sm-3 d-flex j-ce a-ic">
                                                        <input
                                                            type="checkbox"
                                                            checked={preferences[PanoViewerConstants.InvertY] === PanoViewerConstants.On}
                                                            onChange={() => on3DCheckboxChanged(PanoViewerConstants.InvertY)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Camera Type */}
                                            <div className="row">
                                                <label className="col-sm-9 pull-left d-flex a-ic">
                                                    {InovxLoader.t("facility:preferences.cameraType")}
                                                </label>
                                                <div className="col-sm-8">
                                                    <div className="pull-right">
                                                        <select
                                                            className="camera-type-selector"
                                                            id="cameraTypeSelect"
                                                            onChange={onCameraTypeChange}
                                                            value={preferences[PanoViewerConstants.CameraType] || PanoViewerConstants.CameraTypeExamine}
                                                        >
                                                                <option value={PanoViewerConstants.CameraTypeExamine}>{InovxLoader.t('facility:preferences.cameraTypeExamine')}</option>
                                                                <option value={PanoViewerConstants.CameraTypeUniversal}>{InovxLoader.t('facility:preferences.cameraTypeUniversal')}</option>
                                                                <option value={PanoViewerConstants.CameraTypeFly}>{InovxLoader.t('facility:preferences.cameraTypeFly')}</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Rotation Sensitivity (0.5x .. 2x, centered at 1x) */}
                                            <div className="row d-flex a-ic" id="rotationSensitivity" style={{ alignItems: "center" }}>
                                                <label className="col-sm-4 d-flex a-ic" style={{ marginBottom: 0 }}>
                                                    {InovxLoader.t("facility:preferences.rotationSensitivity")}
                                                </label>

                                                <div className="col-sm-8">
                                                    <div className="pull-right" style={{ width: "75%", minWidth: 220 }}>
                                                        <input
                                                            type="range"
                                                            min="-1"
                                                            max="1"
                                                            step="0.05"
                                                            value={preferences[PanoViewerConstants.RotationSensitivity] ?? "0"}
                                                            onChange={onRotationSensitivityChange}
                                                            style={{ width: "100%" }}
                                                        />

                                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, opacity: 0.75 }}>
                                                            <span>{InovxLoader.t('facility:preferences.slower')}</span>
                                                            <span>{InovxLoader.t('facility:preferences.faster')}</span>
                                                        </div>

                                                        <div style={{ textAlign: "right", fontSize: 12 }}>
                                                            {(() => {
                                                                const s = parseFloat(preferences[PanoViewerConstants.RotationSensitivity] ?? "0");
                                                                const mult = Math.pow(2, s);
                                                                return `${mult.toFixed(2)}x`;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Velocity Sensitivity (0.5x .. 5x, centered at 1x) */}
                                            <div className="row d-flex a-ic" id="velocitySensitivity" style={{ alignItems: "center" }}>
                                                <label className="col-sm-4 d-flex a-ic" style={{ marginBottom: 0 }}>
                                                    {InovxLoader.t("facility:preferences.velocitySensitivity")}
                                                </label>

                                                <div className="col-sm-8">
                                                    <div className="pull-right" style={{ width: "75%", minWidth: 220 }}>
                                                        <input
                                                            type="range"
                                                            min="-1"
                                                            max="1"
                                                            step="0.05"
                                                            value={preferences[PanoViewerConstants.VelocitySensitivity] ?? "0"}
                                                            onChange={onVelocitySensitivityChange}
                                                            style={{ width: "100%" }}
                                                        />

                                                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, opacity: 0.75 }}>
                                                            <span>{InovxLoader.t('facility:preferences.slower')}</span>
                                                            <span>{InovxLoader.t('facility:preferences.faster')}</span>
                                                        </div>

                                                        <div style={{ textAlign: "right", fontSize: 12 }}>
                                                            {(() => {
                                                                const s = parseFloat(preferences[PanoViewerConstants.VelocitySensitivity] ?? "0");
                                                                const mult = s < 0 ? Math.pow(2, s) : Math.pow(5, s);
                                                                return `${mult.toFixed(2)}x`;
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>


                                        </>}
                                    </form>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="panel-footer clearfix">
                    <div className="row">
                        <div className="pull-left">
                            <button
                                type="button"
                                id="preferences.btn.reset"
                                className="btn btn-info btn-left"
                                onClick={onReset}
                            >
                                {InovxLoader.t("facility:preferences.resetButton")}
                            </button>
                        </div>
                        <div className="pull-right">
                            <button
                                type="button"
                                id="preferences.btn.save"
                                className="btn btn-primary btn-space"
                                onClick={onSave}
                            >
                                {InovxLoader.t("facility:preferences.saveButton")}
                            </button>
                            <button
                                type="button"
                                id="preferences.btn.cancel"
                                className="btn btn-primary btn-right"
                                onClick={onCancel}
                            >
                                {InovxLoader.t("facility:preferences.cancelButton")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FacilityPreferences;
